import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { AttentionItem, AttentionSeverity } from '@/api/v2Client';

/**
 * Phase dashboard-attention-surface (TODO-024) — "Needs your attention"
 * surface that sits above the dashboard's submissions table. One row per
 * AttentionItem; each row links to the concrete next action (the
 * submission detail page, the compiler, etc.).
 *
 * Severity → left border color:
 *   error   → red    (#dc2626)
 *   warning → yellow (#d97706)
 *   info    → blue   (#2563eb)
 *
 * Severity → icon glyph (kept plain text so screen readers ignore it; the
 * `severity` value itself is exposed via aria-label on the row).
 */

interface AttentionSurfaceProps {
  items: AttentionItem[];
}

const SEVERITY_COLOR: Record<AttentionSeverity, string> = {
  error: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
};

const SEVERITY_ICON: Record<AttentionSeverity, string> = {
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

export function AttentionSurface({ items }: AttentionSurfaceProps): ReactElement | null {
  if (items.length === 0) return null;

  return (
    <section
      data-testid="attention-surface"
      aria-label="Needs your attention"
      style={{ marginBottom: 24 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>Needs your attention</h3>
        <span
          data-testid="attention-surface-count"
          aria-label={`${items.length} item${items.length === 1 ? '' : 's'}`}
          style={{
            background: 'var(--primary, #1d4ed8)',
            color: '#fff',
            fontSize: 12,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 10,
          }}
        >
          {items.length}
        </span>
      </div>

      <ul
        style={{
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        {items.map((item) => {
          const color = SEVERITY_COLOR[item.severity];
          const icon = SEVERITY_ICON[item.severity];
          const label = item.ndaNumber
            ? `${item.drugName} (NDA ${item.ndaNumber}) seq ${item.sequenceNumber}`
            : `${item.drugName} seq ${item.sequenceNumber}`;

          return (
            <li
              key={`${item.submissionId}-${item.type}`}
              data-testid={`attention-item-${item.submissionId}-${item.type}`}
              data-severity={item.severity}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                background: 'var(--surface, #fff)',
                borderLeft: `4px solid ${color}`,
                borderRadius: 4,
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              <span aria-hidden="true" style={{ color, fontSize: 18 }}>
                {icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
                <div style={{ color: 'var(--text-muted, #555)', fontSize: 13 }}>
                  {item.message}
                </div>
              </div>
              <Link
                to={item.actionUrl}
                data-testid={`attention-action-${item.submissionId}-${item.type}`}
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: color,
                  textDecoration: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.actionLabel} →
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
